import { useCallback, useRef } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { useTranslation } from 'react-i18next'

import { useAuthentication } from '@/hooks/useAuthentication'
import { useNavigation } from '@/hooks/useNavigation'
import { useSpace } from '@/hooks/useSpace'
import { useModal } from '@/contexts/modalContext'
import { Settings } from '@/constants'
import { SpaceContextType } from '@/shared/types/space'

const useIdleLogout = () => {
    const { logout } = useAuthentication()
    const { navigateTo } = useNavigation()
    const { switchSpace } = useSpace() as SpaceContextType
    const { showModal, hideModal } = useModal()
    const { t } = useTranslation()

    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const activateRef = useRef<() => void>(() => {})

    const clearCountdown = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }, [])

    const doLogout = useCallback(() => {
        clearCountdown()
        hideModal()
        switchSpace(undefined)
        logout()
        navigateTo('/identity/login')
    }, [clearCountdown, hideModal, logout, navigateTo, switchSpace])

    const onPrompt = useCallback(() => {
        const totalSeconds = Math.ceil(Settings.idlePromptBeforeLogout / 1000)
        let remaining = totalSeconds

        const buildConfig = (secs: number) => ({
            title: `${t('general:idleTimeout:title')} (${secs}s)`,
            content: t('general:idleTimeout:message'),
            variant: 'info' as const,
            confirmLabel: t('general:idleTimeout:stayLoggedIn'),
            cancelLabel: t('general:buttons:logout'),
            onConfirmClick: () => {
                clearCountdown()
                hideModal()
                activateRef.current()
            },
            onCancelClick: () => {
                clearCountdown()
                doLogout()
            },
        })

        showModal(buildConfig(remaining))

        intervalRef.current = setInterval(() => {
            remaining--
            if (remaining <= 0) {
                clearCountdown()
            } else {
                showModal(buildConfig(remaining))
            }
        }, 1000)
    }, [t, showModal, doLogout, hideModal, clearCountdown])

    const { activate } = useIdleTimer({
        timeout: Settings.idleTimeout,
        promptBeforeIdle: Settings.idlePromptBeforeLogout,
        onPrompt,
        onIdle: doLogout,
        crossTab: true,
        leaderElection: true,
        throttle: 500,
    })

    activateRef.current = activate
}

export default useIdleLogout
